import { PlaidKeysSection } from '../components/settings/PlaidKeysSection';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  ButtonGroup,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { useAuthStore } from '../stores/auth-store';
import { httpClient } from '../services/http-client';
import { usePreferencesStore } from '../stores/preferences-store';
import { ProfileService } from '../services/profile-service';
import { MainLayout } from '../components/MainLayout';
import { TwoFactorSetup } from '../components/security/TwoFactorSetup';
import { BaseCurrencyCard } from '../components/BaseCurrencyCard';

/**
 * ProfilePage displays user profile settings and developer preferences.
 *
 * Users can update their display name and toggle developer mode which
 * controls visibility of API-related nav items in the sidebar.
 */
/** Offered session lengths, within the server's 1h..30d bounds. */
const SESSION_OPTIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 8, label: '8 hours' },
  { hours: 24, label: '1 day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
  { hours: 336, label: '14 days' },
  { hours: 720, label: '30 days' },
];

export const ProfilePage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { developerMode, toggleDeveloperMode } = usePreferencesStore();
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session length. Loaded from the server so the control reflects what is
  // actually stored, not a client-side guess.
  const [sessionHours, setSessionHours] = useState(168);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionSaved, setSessionSaved] = useState(false);

  useEffect(() => {
    httpClient.get<{ session_duration_hours: number }>('/session/settings')
      .then((r) => setSessionHours(r.session_duration_hours))
      .catch(() => { /* keep the default; the control still works */ });
  }, []);

  const saveSessionHours = async (hours: number) => {
    setSessionBusy(true);
    setSessionSaved(false);
    try {
      const r = await httpClient.put<{ session_duration_hours: number }>(
        '/session/settings', { session_duration_hours: hours },
      );
      setSessionHours(r.session_duration_hours);
      setSessionSaved(true);
    } catch {
      setError('Failed to save session length');
    } finally {
      setSessionBusy(false);
    }
  };

  useEffect(() => {
    setDisplayName(user?.display_name || '');
  }, [user?.display_name]);

  const handleSave = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      const updatedUser = await ProfileService.updateProfile({
        display_name: displayName || undefined,
      });
      // Reason: Update the auth store so the display name is reflected immediately in the UI
      setUser({ ...user!, display_name: updatedUser.display_name });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your account settings
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Profile updated successfully.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ maxWidth: 600, mb: 3 }}>
        <CardHeader title="Personal Information" />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Email"
              value={user?.email || ''}
              disabled
              fullWidth
              helperText="Email cannot be changed"
            />
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              placeholder="Enter your display name"
              helperText="This name will be shown throughout the app"
              inputProps={{ maxLength: 255 }}
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Security. Rendered as ruled sections rather than a card: this
          follows the newer house style used by the landing and docs pages, and
          the flow needs more room than a card comfortably gives. */}
      <Box sx={{ maxWidth: 600, mb: 4 }}>
        <TwoFactorSetup />
      </Box>

      <Card sx={{ maxWidth: 600, mb: 3 }}>
        <CardHeader title="Session" />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How long you stay signed in before needing to log in again. Your
            session renews automatically while you are using the app.
          </Typography>
          <TextField
            select fullWidth size="small" label="Keep me signed in for"
            value={sessionHours}
            onChange={(e) => saveSessionHours(Number(e.target.value))}
            disabled={sessionBusy}
            helperText={
              sessionSaved
                ? 'Saved — applies from your next sign-in or refresh.'
                : 'Minimum 1 hour, maximum 30 days.'
            }
          >
            {SESSION_OPTIONS.map((o) => (
              <MenuItem key={o.hours} value={o.hours}>
                {o.label}{o.hours === 168 ? ' (default)' : ''}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      <BaseCurrencyCard />

      {/* Paid plans only (#42); renders nothing for free users. */}
      <PlaidKeysSection />

      <Card sx={{ maxWidth: 600 }}>
        <CardHeader title="Developer Settings" />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={developerMode}
                onChange={toggleDeveloperMode}
              />
            }
            label="Developer Mode"
          />
          <Typography variant="body2" color="text.secondary">
            Enable to show API Tokens, Extensions, and API Documentation in the
            navigation menu.
          </Typography>
          {developerMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              To connect an AI assistant, create a dedicated API token and add
              FolioWorth as a remote MCP server. The Extensions guide includes
              the exact URL, authorization header, and configuration to copy.
              <Box sx={{ mt: 1.5 }}>
                <ButtonGroup size="small" variant="outlined">
                  <Button component={Link} to="/extensions">Connection guide</Button>
                  <Button component={Link} to="/api-tokens">Create token</Button>
                </ButtonGroup>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
    </MainLayout>
  );
};
